/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Case, Switch } from "react-if";
import { Box } from "@nordcloud/gnui";
import { BreadcrumbsBox } from "~/components";
import { useQueryState } from "~/hooks";
import {
  EstateChartsQueryState,
  EstateGroupBy,
  useEstateChartParameters,
  EstateFiltersAndChartParams,
} from "./components";
import { EstateRecordsAccumulatedView } from "./EstateRecordsAccumulatedView";
import { EstateRecordsApplicationsView } from "./EstateRecordsApplicationsView";
import { EstateRecordsEnvironmentTypeView } from "./EstateRecordsEnvironmentTypeView";
import { EstateRecordsOrgUnitView } from "./EstateRecordsOrgUnitView";
import { EstateRecordsProviderView } from "./EstateRecordsProviderView";
import { EstateRecordsRegionView } from "./EstateRecordsRegionView";

const BREADCRUMBS = [
  {
    value: "estate-records",
    label: "Estate Records",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

type Props = {
  envNid?: string;
};

export function EstateRecordsPage({ envNid }: Props) {
  const {
    state: { groupBy },
  } = useQueryState<EstateChartsQueryState>();

  const activeChart = groupBy ?? EstateGroupBy.NONE;

  const {
    period,
    range,
    granularity,
    handlePeriodChange,
    handleGranularityChange,
    clearRange,
  } = useEstateChartParameters();

  const [filters, setFilters] = useState({});

  function isChartVisible(chart: EstateGroupBy) {
    return chart === activeChart;
  }

  return (
    <div>
      <BreadcrumbsBox
        title="Estate Records"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <EstateFiltersAndChartParams
          period={period}
          range={range}
          granularity={granularity}
          restFilters={filters}
          disableDailyView={false}
          activeChart={activeChart}
          handleFilters={setFilters}
          clearRange={clearRange}
          onPeriodChange={handlePeriodChange}
          onGranularityChange={handleGranularityChange}
        />
        <Switch>
          <Case condition={isChartVisible(EstateGroupBy.NONE)}>
            <EstateRecordsAccumulatedView
              envNid={envNid}
              period={period}
              range={range}
              granularity={granularity}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.APP)}>
            <EstateRecordsApplicationsView
              granularity={granularity}
              period={period}
              range={range}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ENV)}>
            <EstateRecordsEnvironmentTypeView
              period={period}
              granularity={granularity}
              range={range}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ORG)}>
            <EstateRecordsOrgUnitView
              period={period}
              granularity={granularity}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.PROVIDER)}>
            <EstateRecordsProviderView
              period={period}
              granularity={granularity}
              range={range}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.REGION)}>
            <EstateRecordsRegionView
              period={period}
              granularity={granularity}
              range={range}
            />
          </Case>
        </Switch>
      </Box>
    </div>
  );
}
